import { PylonBrowserTracker, type MetricPayload } from '@hyperconnect/pylon-browser-tracker';
import * as Sentry from '@sentry/nextjs';
import { v4 } from 'uuid';

import appConfig from 'app-config';

import getDeviceInfo from 'src/utils/device/info';

const service_code = appConfig.serviceCode.toUpperCase();

const DEVICE_INFO_SENT_KEY = 'metric_device_info_sent';

let metricOrigin: string | null = null;
let pendingCallback: (() => Promise<void>) | null = null;

export function initializeMetric(origin: string) {
  metricOrigin = origin;

  if (pendingCallback) {
    pendingCallback();
    pendingCallback = null;
  }
}

function getCameraCapability(stream: MediaStream) {
  const videoTrack = stream.getVideoTracks()[0];
  if (!videoTrack || typeof videoTrack.getCapabilities !== 'function') return null;

  const cap = videoTrack.getCapabilities();

  return {
    name: videoTrack.label || null,
    'max_resolution.width': cap.width?.max ?? null,
    'max_resolution.height': cap.height?.max ?? null,
    supports_60fps_at_hd: cap.frameRate?.max != null ? cap.frameRate.max >= 60 : null,
    lens_facing:
      cap.facingMode?.[0] === 'user'
        ? 'front'
        : cap.facingMode?.[0] === 'environment'
          ? 'back'
          : null,
    nominated: true,
  };
}

async function sendMetric(
  metricType: string,
  metricParams: Record<string, unknown>
): Promise<boolean> {
  if (metricOrigin == null) return false;

  const timestamp = Date.now();

  const payload = {
    service_code,
    metric_timestamp: timestamp,
    metric_uid: v4(),
    short_message: JSON.stringify({
      timestamp,
      value: { category: metricType, ...metricParams },
    }),
  } satisfies MetricPayload;

  try {
    const result = await PylonBrowserTracker.getInstance().sendMetric(payload, { retries: 0 });
    if (!result.success) {
      if (result.status !== 0 && result.error == null) {
        Sentry.captureMessage('Metric Gateway Response Not OK', {
          extra: { status: result.status },
        });
      } else {
        Sentry.captureMessage('Metric Gateway Send Failed', {
          extra: { status: result.status, error: result.error },
        });
      }
      return false;
    }

    return true;
  } catch (error) {
    Sentry.captureMessage('Metric Gateway Send Failed', { extra: { error } });
    return false;
  }
}

/**
 * 카메라 권한 획득 시점에 호출한다.
 * localStorage 기반으로 최초 1회만 전송. (https://hpcnt.slack.com/archives/C01D35UT4RW/p1772769786427869?thread_ts=1772766216.478059&cid=C01D35UT4RW)
 *
 * @see https://www.notion.so/hpcnt/Client-Metric-1c0ce00cd354809684bbcab36ffbf963
 */
export function sendDeviceInfo(stream: MediaStream) {
  if (localStorage.getItem(DEVICE_INFO_SENT_KEY)) return;

  const deviceInfo = getDeviceInfo();
  const cameraCapability = getCameraCapability(stream);

  const params: Record<string, unknown> = {
    service_name: appConfig.serviceCode,
    platform: 'Web',
    web_os: deviceInfo.os ?? null,
    brand: deviceInfo.browserName ?? null,
    browser_version: deviceInfo.browserVersion ?? null,
    num_of_cores: navigator.hardwareConcurrency ?? null,
    ram_capacity: (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null,
    features: {},
    camera_capability: cameraCapability ? [cameraCapability] : null,
  };

  const markSent = async () => {
    const success = await sendMetric('device_info', params);
    if (success) {
      localStorage.setItem(DEVICE_INFO_SENT_KEY, 'TRUE');
    }
  };

  if (metricOrigin == null) {
    pendingCallback = markSent;
  } else {
    markSent();
  }
}
